import { Tenant, User, UserRole, TenantRoster, TenantUserAssignment } from '../types/auth';

type DemoAuthResult = { user: User; tenant: Tenant };

// Simple seeded RNG for deterministic mock data
const createRng = (seed: number) => {
  let x = seed;
  return () => {
    x = Math.sin(x) * 10000;
    return x - Math.floor(x);
  };
};

class TenantMockService {
  private rng: () => number;
  private tenants: Tenant[];
  private users: User[];
  private assignments: TenantUserAssignment[];

  constructor(seed = 42) {
    this.rng = createRng(seed);
    this.tenants = this.buildTenants();
    this.users = this.buildUsers();
    this.assignments = this.buildAssignments();
  }

  // ---------- Data builders ----------
  private buildTenants(): Tenant[] {
    const now = new Date().toISOString();
    return [
      { id: 'tenant-alpha', name: 'Alpha Industries', status: 'active', plan: 'enterprise', userCount: 0, maxUsers: 120, createdAt: now, updatedAt: now },
      { id: 'tenant-bravo', name: 'Bravo Retail', status: 'active', plan: 'professional', userCount: 0, maxUsers: 60, createdAt: now, updatedAt: now },
      { id: 'tenant-charlie', name: 'Charlie Health', status: 'active', plan: 'starter', userCount: 0, maxUsers: 25, createdAt: now, updatedAt: now },
      { id: 'tenant-delta', name: 'Delta Consulting', status: 'inactive', plan: 'starter', userCount: 0, maxUsers: 15, createdAt: now, updatedAt: now }
    ];
  }

  private buildUsers(): User[] {
    const now = new Date().toISOString();
    const names: Array<[string, string, UserRole, string]> = [
      ['Alex', 'Stone', 'super_admin', 'CEO'],
      ['Brooke', 'Taylor', 'administrator', 'Director of Ops'],
      ['Casey', 'Lee', 'administrator', 'Director of Sales'],
      ['Drew', 'Nguyen', 'tenant_admin', 'Head of Sales'],
      ['Evan', 'Kim', 'tenant_admin', 'Head of Customer Success'],
      ['Frankie', 'Lopez', 'tenant_admin', 'Head of Delivery'],
      ['Gabby', 'Chen', 'tenant_admin', 'Head of Product'],
      ['Harper', 'Singh', 'manager', 'Sales Manager'],
      ['Indy', 'Morales', 'manager', 'Eng Manager'],
      ['Jordan', 'Patel', 'manager', 'Support Manager'],
      ['Kai', 'Wright', 'manager', 'CX Manager'],
      ['Logan', 'Diaz', 'manager', 'Project Manager'],
      ['Morgan', 'Shaw', 'manager', 'Analytics Manager'],
      ['Nico', 'Rossi', 'user', 'AE'],
      ['Oakley', 'Khan', 'user', 'AE'],
      ['Peyton', 'Brown', 'user', 'AE'],
      ['Quinn', 'Foster', 'user', 'SDR'],
      ['River', 'Carter', 'user', 'SDR'],
      ['Sage', 'Adams', 'user', 'SDR'],
      ['Tatum', 'Baker', 'user', 'CSM'],
      ['Uri', 'Garcia', 'user', 'CSM'],
      ['Vale', 'Hernandez', 'user', 'CX'],
      ['Winter', 'Ibrahim', 'user', 'CX'],
      ['Yuri', 'Johnson', 'user', 'Analyst'],
    ];

    const palette = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#14b8a6', '#a855f7', '#f97316'];
    const locations = ['New York, USA', 'London, UK', 'Toronto, CA', 'Berlin, DE', 'Sydney, AU', 'Austin, USA', 'Singapore', 'Dubai, AE'];
    const departments = ['Executive', 'Operations', 'Sales', 'Customer Success', 'Product', 'Engineering', 'Support', 'Analytics'];
    const timezones = ['America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Singapore', 'Australia/Sydney'];

    return names.map(([first, last, role, title], idx) => {
      const color = palette[idx % palette.length];
      const location = locations[Math.floor(this.rng() * locations.length)];
      const department = departments[Math.floor(this.rng() * departments.length)];
      const timezone = timezones[Math.floor(this.rng() * timezones.length)];
      return {
        id: `user-${idx + 1}`,
        username: `${first.toLowerCase()}.${last.toLowerCase()}`,
        email: `${first.toLowerCase()}.${last.toLowerCase()}@demo.com`,
        role,
        tenantId: 'unassigned',
        firstName: first,
        lastName: last,
        avatar: undefined,
        avatarColor: color,
        status: 'active',
        lastLogin: now,
        title,
        department,
        location,
        phone: `+1-555-01${(idx + 1).toString().padStart(2, '0')}`,
        timezone,
        bio: `${title} focusing on multi-tenant CRM excellence.`,
        createdAt: now,
        updatedAt: now
      };
    });
  }

  private buildAssignments(): TenantUserAssignment[] {
    const tenantIds = this.tenants.map(t => t.id);
    const rolePriority: UserRole[] = ['super_admin', 'administrator', 'tenant_admin', 'manager', 'user'];

    const assignments: TenantUserAssignment[] = [];
    this.users.forEach((user, index) => {
      // assign most users to one tenant, some to two, and a few unassigned
      if (index % 7 === 0) {
        // keep unassigned to demonstrate assignment flow
        return;
      }
      const tenantId = tenantIds[index % tenantIds.length];
      const status = index % 9 === 0 ? 'invited' : 'active';
      const lastActive = new Date(Date.now() - Math.floor(this.rng() * 14) * 24 * 60 * 60 * 1000).toISOString();
      assignments.push({
        tenantId,
        userId: user.id,
        role: user.role,
        status,
        lastActive,
        assignedAt: new Date(Date.now() - Math.floor(this.rng() * 30) * 24 * 60 * 60 * 1000).toISOString(),
        assignedBy: assignments.length ? assignments[0].userId : 'user-1'
      });

      // small chance for a second tenant membership for admins/managers
      if (rolePriority.indexOf(user.role) <= 2 && this.rng() > 0.7) {
        const other = tenantIds[(index + 1) % tenantIds.length];
        assignments.push({
          tenantId: other,
          userId: user.id,
          role: user.role === 'super_admin' ? 'administrator' : user.role,
          status,
          lastActive,
          assignedAt: new Date(Date.now() - Math.floor(this.rng() * 30) * 24 * 60 * 60 * 1000).toISOString(),
          assignedBy: assignments[0]?.userId || 'user-1'
        });
      }
    });

    // update each user's primary tenantId when assigned
    assignments.forEach(a => {
      const user = this.users.find(u => u.id === a.userId);
      if (user && user.tenantId === 'unassigned') {
        user.tenantId = a.tenantId;
      }
    });

    return assignments;
  }

  // ---------- Derived helpers ----------
  private withComputedCounts(): Tenant[] {
    return this.tenants.map(t => {
      const count = this.assignments.filter(a => a.tenantId === t.id).length;
      return { ...t, userCount: count };
    });
  }

  private roleDistribution(tenantId: string) {
    const roles: UserRole[] = ['super_admin', 'administrator', 'tenant_admin', 'manager', 'user'];
    return roles.map(role => ({
      role,
      count: this.assignments.filter(a => a.tenantId === tenantId && a.role === role).length
    }));
  }

  private findUser(userId: string) {
    const user = this.users.find(u => u.id === userId);
    if (!user) throw new Error('User not found');
    return user;
  }

  private findTenant(tenantId: string) {
    const tenant = this.tenants.find(t => t.id === tenantId);
    if (!tenant) throw new Error('Tenant not found');
    return tenant;
  }

  // ---------- Public API ----------
  async listTenants(): Promise<Tenant[]> {
    return this.withComputedCounts();
  }

  async listUsers(): Promise<User[]> {
    return [...this.users];
  }

  async getTenantRoster(tenantId: string): Promise<TenantRoster> {
    const tenant = this.findTenant(tenantId);
    const rosterAssignments = this.assignments.filter(a => a.tenantId === tenantId);
    const roster = rosterAssignments.map(a => ({
      assignment: a,
      user: this.findUser(a.userId)
    }));
    const unassignedUsers = this.users.filter(u => !this.assignments.some(a => a.userId === u.id && a.tenantId === tenantId));

    return {
      tenant: { ...tenant, userCount: rosterAssignments.length },
      roster,
      unassignedUsers,
      roleDistribution: this.roleDistribution(tenantId)
    };
  }

  async assignUserToTenant(tenantId: string, userId: string, role: UserRole): Promise<TenantRoster> {
    const existing = this.assignments.find(a => a.tenantId === tenantId && a.userId === userId);
    if (existing) {
      existing.role = role;
      existing.status = 'active';
      existing.assignedAt = new Date().toISOString();
    } else {
      this.assignments.push({
        tenantId,
        userId,
        role,
        status: 'active',
        lastActive: new Date().toISOString(),
        assignedAt: new Date().toISOString(),
        assignedBy: 'user-1'
      });
    }

    // ensure primary tenant id is set
    const user = this.findUser(userId);
    if (user.tenantId === 'unassigned') {
      user.tenantId = tenantId;
    }

    return this.getTenantRoster(tenantId);
  }

  async unassignUserFromTenant(tenantId: string, userId: string): Promise<TenantRoster> {
    this.assignments = this.assignments.filter(a => !(a.tenantId === tenantId && a.userId === userId));
    return this.getTenantRoster(tenantId);
  }

  async changeTenantUserRole(tenantId: string, userId: string, role: UserRole): Promise<TenantRoster> {
    const assignment = this.assignments.find(a => a.tenantId === tenantId && a.userId === userId);
    if (!assignment) throw new Error('Assignment not found');
    assignment.role = role;
    assignment.updatedAt = new Date().toISOString();
    return this.getTenantRoster(tenantId);
  }

  async getUserProfile(userId: string): Promise<{ user: User; memberships: TenantUserAssignment[] }> {
    const user = this.findUser(userId);
    const memberships = this.assignments.filter(a => a.userId === userId);
    return { user, memberships };
  }

  async loginAs(usernameOrId: string): Promise<DemoAuthResult> {
    const user = this.users.find(
      u => u.username === usernameOrId || u.id === usernameOrId || `${u.firstName?.toLowerCase()}.${u.lastName?.toLowerCase()}` === usernameOrId
    ) || this.users[0];

    const firstMembership = this.assignments.find(a => a.userId === user.id);
    const tenant = firstMembership ? this.findTenant(firstMembership.tenantId) : this.tenants[0];

    return { user, tenant };
  }
}

export const tenantMockService = new TenantMockService();
